import cn from "classnames";
import { ReactNode } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  AiLessonGuidanceIssue,
  AiLessonMessageType,
  EAiLessonCategory
} from "../../aiAgents.types";
import {
  AI_LESSON_LIMITS,
  AI_LESSON_MESSAGES,
  AI_LESSON_MESSAGE_TYPES,
  AI_LESSON_NON_DISCRIMINATING_TAGS,
  AI_LESSON_TAGS
} from "../../constants/lessons";
import {
  LessonFormValue,
  addLessonKeyword,
  guidanceIssueLabelKey,
  setLessonCorrectedReply,
  toggleLessonTag
} from "../../helpers/lessons";
import { ChipEditor } from "../ChipEditor";

import styles from "./LessonForm.module.scss";

interface Props {
  value: LessonFormValue;
  onChange: (next: LessonFormValue) => void;
  // Guidance words the server refused on save.
  issues?: AiLessonGuidanceIssue[];
  // Words the AI draft flagged; saving is still allowed to try.
  warnings?: AiLessonGuidanceIssue[];
  mode: "create" | "edit";
  disabled?: boolean;
  // Shown right after the correction note: the "Suggest 2 versions" button and
  // its A/B cards, which need the conversation, so the caller owns them.
  suggestSlot?: ReactNode;
}

const Field = ({
  label,
  help,
  children
}: {
  label: string;
  help?: string;
  children: ReactNode;
}) => (
  <div className={styles.field}>
    <div className={styles.label}>{label}</div>
    {help ? <div className={styles.help}>{help}</div> : null}
    {children}
  </div>
);

const IssueList = ({
  title,
  issues,
  tone
}: {
  title: string;
  issues?: AiLessonGuidanceIssue[];
  tone: "error" | "warning";
}) => {
  const { t } = useTranslation();
  if (!issues || issues.length === 0) return null;

  return (
    <div
      className={cn(styles.issues, {
        [styles.issuesError]: tone === "error",
        [styles.issuesWarning]: tone === "warning"
      })}
    >
      <div className={styles.issuesTitle}>{title}</div>
      <ul className={styles.issuesList}>
        {issues.map((issue, index) => (
          <li key={`${issue.rule}-${index}`}>
            {issue.match
              ? t("aiLesson.form.issueMatch", {
                  rule: t(guidanceIssueLabelKey(issue.rule)),
                  match: issue.match
                })
              : t(guidanceIssueLabelKey(issue.rule))}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Controlled lesson form, shared by the correction panel (create) and the
// Lessons tab (edit). Only the guidance is ever sent to the assistant; X and
// Y stay for display and replay.
const LessonForm = ({
  value,
  onChange,
  issues,
  warnings,
  mode,
  disabled,
  suggestSlot
}: Props) => {
  const { t } = useTranslation();
  const isEdit = mode === "edit";

  const set = <K extends keyof LessonFormValue>(
    key: K,
    next: LessonFormValue[K]
  ) => onChange({ ...value, [key]: next });

  const handleAddKeyword = (raw: string) => {
    const result = addLessonKeyword(value.keywords, raw);
    if (result.ok) {
      set("keywords", result.keywords);
    } else if (result.reason === "invalid") {
      toast.error(t(AI_LESSON_MESSAGES.KEYWORDS));
    }
  };

  const toggleMessageType = (type: AiLessonMessageType) =>
    set(
      "messageTypes",
      value.messageTypes.includes(type)
        ? value.messageTypes.filter((m) => m !== type)
        : AI_LESSON_MESSAGE_TYPES.filter(
            (m) => m === type || value.messageTypes.includes(m)
          )
    );

  const tagsFull = value.tags.length >= AI_LESSON_LIMITS.TAGS.MAX;

  return (
    <div className={styles.form}>
      <Field label={t("aiLesson.form.badReply")}>
        <textarea
          className={cn(styles.textarea, styles.readOnly)}
          value={value.badReply}
          rows={3}
          readOnly
        />
      </Field>

      {!isEdit && (
        <Field label={t("aiLesson.form.correction")}>
          <textarea
            className={styles.textarea}
            value={value.correction}
            onChange={(e) => set("correction", e.target.value)}
            placeholder={t("aiLesson.form.correctionPlaceholder")}
            maxLength={AI_LESSON_LIMITS.CORRECTION_NOTE.MAX_LENGTH}
            rows={2}
            disabled={disabled}
          />
        </Field>
      )}

      {suggestSlot}

      <Field label={t("aiLesson.form.correctedReply")}>
        <textarea
          className={cn(styles.textarea, { [styles.readOnly]: isEdit })}
          value={value.correctedReply}
          onChange={(e) =>
            onChange(setLessonCorrectedReply(value, e.target.value))
          }
          maxLength={AI_LESSON_LIMITS.REPLY.MAX_LENGTH}
          rows={3}
          readOnly={isEdit}
          disabled={disabled}
        />
      </Field>

      <Field
        label={t("aiLesson.form.guidance")}
        help={t("aiLesson.form.guidanceHelp")}
      >
        <textarea
          className={styles.textarea}
          value={value.guidance}
          onChange={(e) => set("guidance", e.target.value)}
          maxLength={AI_LESSON_LIMITS.GUIDANCE.MAX_LENGTH}
          rows={3}
          disabled={disabled}
        />
        <div className={styles.counter}>
          {t("aiLesson.form.guidanceCounter", {
            count: value.guidance.length
          })}
        </div>
        <IssueList
          title={t("aiLesson.form.issuesTitle")}
          issues={issues}
          tone="error"
        />
        <IssueList
          title={t("aiLesson.form.warningsTitle")}
          issues={warnings}
          tone="warning"
        />
      </Field>

      <Field label={t("aiLesson.form.category")}>
        <select
          className={styles.select}
          value={value.category}
          onChange={(e) => set("category", e.target.value as EAiLessonCategory)}
          disabled={disabled}
        >
          {Object.values(EAiLessonCategory).map((category) => (
            <option key={category} value={category}>
              {t(`aiLesson.category.${category}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t("aiLesson.form.title")}>
        <input
          className={styles.input}
          value={value.title}
          onChange={(e) => set("title", e.target.value)}
          maxLength={AI_LESSON_LIMITS.TITLE.MAX_LENGTH}
          disabled={disabled}
        />
      </Field>

      <Field label={t("aiLesson.form.situation")}>
        <textarea
          className={styles.textarea}
          value={value.situation}
          onChange={(e) => set("situation", e.target.value)}
          placeholder={t("aiLesson.form.situationPlaceholder")}
          maxLength={AI_LESSON_LIMITS.SITUATION.MAX_LENGTH}
          rows={2}
          disabled={disabled}
        />
      </Field>

      <Field label={t("aiLesson.form.tags")} help={t("aiLesson.form.tagsHelp")}>
        <div className={styles.tags}>
          {AI_LESSON_TAGS.map((tag) => {
            const selected = value.tags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                className={cn(styles.tag, {
                  [styles.tagSelected]: selected,
                  [styles.tagSecondary]:
                    AI_LESSON_NON_DISCRIMINATING_TAGS.includes(tag)
                })}
                aria-pressed={selected}
                disabled={disabled || (!selected && tagsFull)}
                onClick={() => set("tags", toggleLessonTag(value.tags, tag))}
              >
                {t(`aiLesson.tag.${tag}`)}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label={t("aiLesson.form.keywords")}>
        <ChipEditor
          placeholder={t("aiLesson.form.keywordsPlaceholder")}
          variant="neutral"
          items={value.keywords.map((keyword) => ({
            id: keyword,
            label: keyword
          }))}
          disabled={disabled}
          onAdd={handleAddKeyword}
          onRemove={(keyword) =>
            set(
              "keywords",
              value.keywords.filter((k) => k !== keyword)
            )
          }
        />
      </Field>

      <Field label={t("aiLesson.form.messageTypes")}>
        <div className={styles.checkboxes}>
          {AI_LESSON_MESSAGE_TYPES.map((type) => (
            <label key={type} className={styles.checkbox}>
              <input
                type="checkbox"
                checked={value.messageTypes.includes(type)}
                onChange={() => toggleMessageType(type)}
                disabled={disabled}
              />
              <span>{t(`aiLesson.messageType.${type}`)}</span>
            </label>
          ))}
        </div>
      </Field>

      <p className={styles.safetyNote}>{t("aiLesson.form.safetyNote")}</p>
    </div>
  );
};

export default LessonForm;
